import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import type { Database } from "../db/types.ts";
import { getBrowserClient } from "../supabase/browser.ts";

type KnowledgePathRow = Database["public"]["Tables"]["knowledge_paths"]["Row"];
type KnowledgeLessonRow = Database["public"]["Tables"]["knowledge_lessons"]["Row"];
type _KnowledgeProgressRow = Database["public"]["Tables"]["knowledge_progress"]["Row"];

export interface LessonWithProgress extends KnowledgeLessonRow {
  completed: boolean;
}

export interface PathWithLessons extends KnowledgePathRow {
  lessons: LessonWithProgress[];
  completedCount: number;
}

export interface UseKnowledgePathsState {
  paths: PathWithLessons[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useKnowledgePaths(): UseKnowledgePathsState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [paths, setPaths] = useState<PathWithLessons[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setPaths([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const [pathsRes, lessonsRes, progressRes] = await Promise.all([
      supabase
        .from("knowledge_paths")
        .select("*")
        .eq("is_published", true)
        .order("sort_order", { ascending: true }),
      supabase.from("knowledge_lessons").select("*").order("sort_order", { ascending: true }),
      supabase.from("knowledge_progress").select("*").eq("user_id", user.id),
    ]);

    if (pathsRes.error) {
      setPaths([]);
      setError(pathsRes.error.message);
      setLoading(false);
      return;
    }
    if (lessonsRes.error) {
      setPaths([]);
      setError(lessonsRes.error.message);
      setLoading(false);
      return;
    }
    if (progressRes.error) {
      setPaths([]);
      setError(progressRes.error.message);
      setLoading(false);
      return;
    }

    const rawPaths = pathsRes.data ?? [];
    const rawLessons = lessonsRes.data ?? [];
    const rawProgress = progressRes.data ?? [];

    const completedSet = new Set(
      rawProgress.filter((p) => p.completed_at !== null).map((p) => p.lesson_id),
    );

    const combined: PathWithLessons[] = rawPaths.map((path) => {
      const lessons: LessonWithProgress[] = rawLessons
        .filter((l) => l.path_id === path.id)
        .map((l) => ({
          ...l,
          completed: completedSet.has(l.id),
        }));
      const completedCount = lessons.filter((l) => l.completed).length;
      return { ...path, lessons, completedCount };
    });

    setPaths(combined);
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<UseKnowledgePathsState>(
    () => ({ paths, loading, error, refetch }),
    [paths, loading, error, refetch],
  );
}
